"use client";

import { useIsShopsite } from "@context/DomainContext";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Popover as RadixPopover } from "@radix-ui/themes";
import { AnimatePresence, motion } from "framer-motion";
import { ComponentProps } from "react";

const PopoverRoot = ({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) => {
  const isShopsite = useIsShopsite();

  if (isShopsite) {
    return <PopoverPrimitive.Root {...props} />;
  }

  return <RadixPopover.Root {...props} />;
};

const PopoverTrigger = ({
  ref,
  ...props
}: ComponentProps<typeof PopoverPrimitive.Trigger>) => {
  const isShopsite = useIsShopsite();

  if (isShopsite) {
    return <PopoverPrimitive.Trigger ref={ref} {...props} />;
  }

  return <RadixPopover.Trigger ref={ref} {...props} />;
};

const PopoverContent = ({
  children,
  open,
  ref,
  ...props
}: ComponentProps<typeof PopoverPrimitive.Content> & {
  open: boolean;
}) => {
  const isShopsite = useIsShopsite();

  if (isShopsite) {
    return (
      <AnimatePresence>
        {open && (
          <PopoverPrimitive.Content
            asChild
            forceMount
            ref={ref}
            align="end"
            sideOffset={15}
            alignOffset={-16}
            className="relative z-50"
            {...props}
          >
            <motion.div
              initial="hidden"
              animate="show"
              exit="hidden"
              variants={{
                hidden: {
                  opacity: 0,
                  scale: 0.95,
                  y: "-0.5rem",
                  x: "0.5rem",
                  transition: { duration: 0.125 },
                },
                show: { opacity: 1, scale: 1, y: 0, x: 0 },
              }}
              transition={{ duration: 0.2 }}
              className="flex flex-col rounded-2xl px-2 py-1.5 bg-white text-black ring-1 ring-inset ring-gray-100 shadow min-w-[300px]"
            >
              {children}
            </motion.div>
          </PopoverPrimitive.Content>
        )}
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      {open && (
        <RadixPopover.Content
          asChild
          forceMount
          ref={ref}
          align="end"
          sideOffset={15}
          alignOffset={-42}
          {...props}
        >
          <motion.div
            initial="hidden"
            animate="show"
            exit="hidden"
            variants={{
              hidden: {
                opacity: 0,
                transition: { duration: 0.125 },
              },
              show: { opacity: 1 },
            }}
            transition={{ duration: 0.25 }}
          >
            {children}
          </motion.div>
        </RadixPopover.Content>
      )}
    </AnimatePresence>
  );
};

export { PopoverRoot, PopoverContent, PopoverTrigger };
